/**
 * Dyte Meeting Adapter
 *
 * Wraps Dyte SDK to implement the IMeetingAdapter interface.
 * This allows Dyte-specific code to be isolated and swappable.
 */

import { useMemo } from 'react';
import { useRealtimeKitMeeting, useRealtimeKitSelector } from '@cloudflare/realtimekit-react';
import { PRESET_TYPE } from 'configs/meeting';
import type {
  IMeetingAdapter,
  IMeetingParticipant,
  IParticipantsCollection,
  IClassState,
} from './MeetingAdapter';

/**
 * Converts a Dyte participant to the generic IMeetingParticipant interface
 */
const convertDyteParticipant = (dyteParticipant: unknown): IMeetingParticipant => {
  // Type guard to ensure dyteParticipant is an object
  const participant = dyteParticipant as Record<string, unknown>;

  return {
    customParticipantId: (participant.customParticipantId || participant.id || '') as string,
    userId: (participant.userId || participant.id || '') as string,
    id: (participant.id || '') as string,
    name: (participant.name || '') as string,
    audioEnabled: (participant.audioEnabled || false) as boolean,
    videoEnabled: (participant.videoEnabled || false) as boolean,
    screenShareEnabled: (participant.screenShareEnabled || false) as boolean,
    presetName: (participant.presetName || '') as string,
    audioTrack: (participant.audioTrack || null) as MediaStreamTrack | null,
    videoTrack: (participant.videoTrack || null) as MediaStreamTrack | null,
    screenShareTrack: ((participant.screenShareTracks as { video?: MediaStreamTrack } | undefined)?.video || null) as MediaStreamTrack | null,
    metadata: participant.metadata,

    // Store reference to original Dyte participant for use with Dyte UI components
    _raw: dyteParticipant,

    // Control methods (for self participant these would work, for remote they're no-ops)
    enableAudio: async () => {
      if (typeof participant.enableAudio === 'function') {
        await (participant.enableAudio as () => Promise<void>)();
      }
    },
    disableAudio: async () => {
      if (typeof participant.disableAudio === 'function') {
        await (participant.disableAudio as () => Promise<void>)();
      }
    },
    enableVideo: async () => {
      if (typeof participant.enableVideo === 'function') {
        await (participant.enableVideo as () => Promise<void>)();
      }
    },
    disableVideo: async () => {
      if (typeof participant.disableVideo === 'function') {
        await (participant.disableVideo as () => Promise<void>)();
      }
    },
    setDevice: async (device: MediaDeviceInfo) => {
      if (typeof participant.setDevice === 'function') {
        await (participant.setDevice as (device: MediaDeviceInfo) => Promise<void>)(device);
      }
    },
  };
};

/**
 * Hook that provides the Dyte meeting adapter
 * Implements the IMeetingAdapter interface using Dyte SDK
 */
export const useDyteMeetingAdapter = (): IMeetingAdapter => {
  const { meeting } = useRealtimeKitMeeting();

  // Self participant
  const dyteSelf = useRealtimeKitSelector((m) => m?.self);
  const self = useMemo(
    () => (dyteSelf ? convertDyteParticipant(dyteSelf) : null),
    [dyteSelf]
  );

  // All joined participants
  const dyteJoinedParticipants = useRealtimeKitSelector((m) => m?.participants?.joined?.toArray() || []);
  const allParticipants = useMemo(
    () => dyteJoinedParticipants.map(convertDyteParticipant),
    [dyteJoinedParticipants]
  );

  // Tutors
  const tutors = useMemo(
    () => allParticipants.filter((p) => p.presetName === PRESET_TYPE.TUTOR),
    [allParticipants]
  );

  // Students
  const students = useMemo(
    () => allParticipants.filter((p) => p.presetName === PRESET_TYPE.STUDENT),
    [allParticipants]
  );

  // Students without self (same as students for recorder)
  const studentsWithOutSelf = useMemo(
    () => students.filter((p) => p.id !== self?.id),
    [students, self]
  );

  // Screen share detection
  const screenShareHost = useRealtimeKitSelector((m) => {
    if (m?.self?.screenShareEnabled) return m.self;
    return m?.participants?.joined?.toArray()?.find((p) => p.screenShareEnabled);
  });
  const convertedScreenShareHost = useMemo(
    () => (screenShareHost ? convertDyteParticipant(screenShareHost) : null),
    [screenShareHost]
  );

  const screenShareEnabled = useRealtimeKitSelector((m) => {
    return (
      !!m?.participants?.joined?.toArray()?.find((p) => p.screenShareEnabled) ||
      m?.self?.screenShareEnabled
    );
  });

  // Plugins
  const activePlugins = useRealtimeKitSelector((m) => m?.plugins?.active?.toArray() || []);
  const allPlugins = useRealtimeKitSelector((m) => m?.plugins?.all?.toArray() || []);

  // Participants collection
  const participants: IParticipantsCollection = useMemo(
    () => ({
      toArray: () => allParticipants,
      size: allParticipants.length,
    }),
    [allParticipants]
  );

  // Class state (default to TEACH for recorder)
  const classState: IClassState = useMemo(
    () => ({
      type: 'teach',
      level: null,
    }),
    []
  );

  // Socket user type
  const socketUser = self?.presetName === PRESET_TYPE.VIEWER ? PRESET_TYPE.VIEWER : 'recorder';

  return useMemo(
    () => ({
      // Self
      self,
      selfId: self?.customParticipantId || '',
      selfName: self?.name || '',

      // Meeting state
      roomState: meeting && self ? 'joined' : 'idle',
      audioStatus: self?.audioEnabled || false,
      videoStatus: self?.videoEnabled || false,

      // Participants
      participants,

      // Derived lists
      tutors,
      students,
      studentsWithOutSelf,
      studentsCount: students.length,

      // Tutor info
      tutorName: tutors[0]?.name || '',
      tutorId: tutors[0]?.id || '',

      // Screen share
      screenShareHost: convertedScreenShareHost,
      screenShareEnabled,

      // Plugins
      activePlugins,
      allPlugins,

      // Class state
      classState,
      isTutorInMainRoom: tutors.length > 0,
      isHostPresent: tutors.length > 0,

      // User info
      loggedInUser: self?.presetName || '',
      socketUser,
      studentDetails: null,
      subscribedSubjects: [],
      studentGroup: [],
      studentName: self?.name || '',

      // Native meeting object
      meeting,
    }),
    [
      self,
      meeting,
      participants,
      tutors,
      students,
      studentsWithOutSelf,
      convertedScreenShareHost,
      screenShareEnabled,
      activePlugins,
      allPlugins,
      classState,
      socketUser,
    ]
  );
};
